import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  IconButton,
  Chip,
  Fade,
  useTheme,
  Tooltip,
} from '@mui/material';
import {
  Close as CloseIcon,
  Minimize as MinimizeIcon,
  Expand as ExpandIcon,
  Analytics as AnalysisIcon,
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch, selectActiveAnalysis, selectAnalysisProgress, setProgressModalOpen } from '../../store';

interface FloatingProgressWidgetProps {
  onClose: () => void;
}

const FloatingProgressWidget: React.FC<FloatingProgressWidgetProps> = ({ onClose }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const activeAnalysis = useAppSelector(selectActiveAnalysis);
  const progress = useAppSelector(selectAnalysisProgress);
  
  const [isMinimized, setIsMinimized] = useState(false);

  // Calculate overall progress
  const overallProgress = progress['overall']?.progress || 0;
  const overallStatus = progress['overall']?.status || 'pending';
  
  // Get current working tool
  const getCurrentTool = () => {
    if (!activeAnalysis?.selectedTools) return null;
    
    for (let i = 0; i < activeAnalysis.selectedTools.length; i++) {
      const tool = activeAnalysis.selectedTools[i];
      const toolProgress = progress[tool];
      if (toolProgress?.status === 'in_progress') {
        return tool;
      }
    }
    return null;
  };

  const currentTool = getCurrentTool();
  const completedTools = activeAnalysis?.selectedTools.filter(tool => 
    progress[tool]?.status === 'completed'
  ).length || 0;

  // Format tool name for display
  const formatToolName = (tool: string) => {
    const toolNames: Record<string, string> = {
      propertyParser: 'Property Parser',
      floorPlan: 'Floor Plan',
      cma: 'Market Analysis',
      neighborhood: 'Neighborhood',
      pricing: 'Pricing Strategy',
      negotiation: 'Negotiation',
      listingDescription: 'Listing Description',
      clientConsultation: 'Client Consultation',
      leadQualification: 'Lead Qualification',
      transactionManagement: 'Transaction Mgmt',
      preTourPrep: 'Pre-Tour Prep',
      clientMatch: 'Client Match',
      buyerSentiment: 'Buyer Sentiment',
      agentOrchestrator: 'Agent Orchestrator',
    };
    return toolNames[tool] || tool;
  };

  // Handle expand to full modal
  const handleExpand = () => {
    dispatch(setProgressModalOpen(true));
  };

  if (!activeAnalysis || overallStatus === 'completed') {
    return null;
  }

  return (
    <Fade in={true}>
      <Box
        sx={{
          position: 'fixed',
          bottom: theme.spacing(2),
          right: theme.spacing(2),
          zIndex: theme.zIndex.modal - 1,
          width: isMinimized ? 200 : 320,
          transition: 'all 0.3s ease-in-out',
        }}
      >
        <Card
          elevation={8}
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(18, 18, 18, 0.95)' : 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: `1px solid ${theme.palette.primary.main}`,
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            {/* Header */}
            <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
              <Box display="flex" alignItems="center" gap={1}>
                <AnalysisIcon color="primary" fontSize="small" />
                {!isMinimized && (
                  <Typography variant="subtitle2" fontWeight="bold">
                    Analyzing Property
                  </Typography>
                )}
              </Box>
              
              <Box display="flex" alignItems="center" gap={0.5}>
                <Tooltip title="Expand to full view">
                  <IconButton size="small" onClick={handleExpand}>
                    <ExpandIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={isMinimized ? "Expand widget" : "Minimize widget"}>
                  <IconButton size="small" onClick={() => setIsMinimized(!isMinimized)}>
                    {isMinimized ? <ExpandIcon fontSize="small" /> : <MinimizeIcon fontSize="small" />}
                  </IconButton>
                </Tooltip>
                <Tooltip title="Close widget">
                  <IconButton size="small" onClick={onClose}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Progress Content */}
            {!isMinimized && (
              <>
                {/* Progress Bar */}
                <Box sx={{ mb: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      Overall Progress
                    </Typography>
                    <Chip
                      label={`${Math.round(overallProgress)}%`}
                      size="small"
                      color="primary"
                      sx={{ fontSize: '0.7rem', height: 20 }}
                    />
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={overallProgress}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': {
                        borderRadius: 3,
                        backgroundColor: theme.palette.primary.main,
                      },
                    }}
                  />
                </Box>

                {/* Current Status */}
                <Box sx={{ mb: 1 }}>
                  <Typography variant="body2" fontWeight="medium" sx={{ mb: 0.5 }}>
                    🚀 {completedTools}/{activeAnalysis.selectedTools.length} tools completed
                  </Typography>
                  
                  {currentTool && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Box
                        sx={{
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: theme.palette.warning.main,
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': {
                            '0%': { opacity: 1 },
                            '50%': { opacity: 0.5 },
                            '100%': { opacity: 1 },
                          },
                        }}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Working on: {formatToolName(currentTool)}
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Property URL */}
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {activeAnalysis.url}
                </Typography>
              </>
            )}

            {/* Minimized View */}
            {isMinimized && (
              <Box display="flex" alignItems="center" gap={1}>
                <LinearProgress
                  variant="determinate"
                  value={overallProgress}
                  sx={{
                    flex: 1,
                    height: 4,
                    borderRadius: 2,
                    backgroundColor: theme.palette.grey[200],
                    '& .MuiLinearProgress-bar': {
                      borderRadius: 2,
                      backgroundColor: theme.palette.primary.main,
                    },
                  }}
                />
                <Typography variant="caption" sx={{ minWidth: 'fit-content' }}>
                  {Math.round(overallProgress)}%
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
};

export default FloatingProgressWidget;